/* eslint-disable react-refresh/only-export-components */
import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";
import { useActionLogger } from "@/services/logger/logger-hooks";
import {
  getFormContextFromFormElement,
  loggerContextManager,
} from "@/services/logger/smart-context-provider";
import { useCallback, useState } from "react";
import type {
  LogActionTypes,
  LogCategories,
  LogSubCategories,
  LogTags,
} from "@/services/logger/logger-types";
import { LogEventTypeEnum } from "@/services/logger/logger-types";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground shadow hover:bg-primary-selected",
        destructive:
          "hover:bg-destructive/90 bg-destructive text-destructive-foreground shadow-sm",
        outline:
          "border border-input bg-background shadow-sm hover:bg-accent hover:text-accent-foreground",
        secondary:
          "hover:bg-secondary/80 bg-secondary text-secondary-foreground shadow-sm",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-9 px-4 py-2",
        sm: "h-8 rounded-md px-3 text-xs",
        lg: "h-10 rounded-md px-8",
        icon: "size-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export type ButtonProps = {
  asChild?: boolean;
  /** When provided, renders as <a> instead of <button> with logging on click */
  href?: string;
  /**
   * When rendering as an anchor (href), choose whether it should look like a normal
   * text link or a button.
   * @default "link"
   */
  linkStyle?: "link" | "button";
  target?: React.AnchorHTMLAttributes<HTMLAnchorElement>["target"];
  rel?: string;
  /**
   * Button type for logging distinction
   * @default "button"
   */
  type?: "button" | "submit" | "reset";
  /**
   * OPTIONAL: Override auto-detected action name
   * If not provided, will use button text or "BUTTON_CLICKED" / "LINK_CLICKED"
   * @example logAction="DELETE_PROFILE" or logAction="WEBSITE_LINK_CLICKED"
   */
  logAction?: LogActionTypes | string;
  /**
   * OPTIONAL: Override tag. Defaults based on action analysis
   * @example logCategory="TAG_1"
   */
  logTag?: LogTags;
  /**
   * OPTIONAL: Override category. Defaults based on action analysis
   * @example logCategory="CRITICAL_ACTION"
   */
  logCategory?: LogCategories;
  /**
   * OPTIONAL: Override category. Defaults based on action analysis
   * @example logSubCategory="CRITICAL_ACTION"
   */
  logSubCategory?: LogSubCategories;
  /**
   * OPTIONAL: Additional metadata to log
   * @example logMetadata={{ itemId: "123", action: "delete" }}
   */
  logMetadata?: Record<string, unknown>;
  /**
   * OPTIONAL: Link metadata for logging.
   * When provided, Button will enrich context with linkType/linkValue/linkSource.
   */
  logLinkMeta?: {
    type: "phone" | "email" | "website";
    value?: string;
    source?: string;
  };
  /**
   * Disable logging for this button
   * @default false
   */
  disableLogging?: boolean;
  /**
   * Debounce time to prevent duplicate clicks
   * @default 300
   */
  logDebounceMs?: number;
  /**
   * Callback when logging fails
   */
  onLogError?: (error: Error) => void;
} & React.ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof buttonVariants>;

const Button = React.forwardRef<
  HTMLButtonElement | HTMLAnchorElement,
  ButtonProps
>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      type = "button",
      href,
      linkStyle = "link",
      target,
      rel,
      // Logging props
      logAction,
      logTag,
      logCategory,
      logSubCategory,
      logMetadata = {},
      logLinkMeta,
      disableLogging = false,
      logDebounceMs = 300,
      onLogError: _onLogError,
      onClick,
      disabled = false,
      children,
      "aria-label": ariaLabel,
      title,
      ...props
    },
    ref,
  ) => {
    const [isLoading, setIsLoading] = useState(false);
    const { logAction: logUserAction } = useActionLogger();
    // TODO: MAYBE ADD useErrorLogger() to log errors

    const isLink = Boolean(href);
    const Comp = asChild && !isLink ? Slot : isLink ? "a" : "button";

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const dataTestId = (props as any)?.["data-testid"] as string | undefined;

    /**
     * Get display label for logging. Priority: test id > aria-label > children (string)
     */
    const getButtonLabel = (): string => {
      if (dataTestId) return dataTestId.split("-").join(" ");
      if (ariaLabel && typeof ariaLabel === "string") return ariaLabel;
      // Instead used test-id for title
      // if (title && typeof title === "string") return title;
      if (typeof children === "string") return children;
      return "";
    };

    /**
     * Generate action name. Prefer test id when available so titles
     * align with QC automation identifiers.
     */
    const getActionName = (): string => {
      if (dataTestId) return dataTestId;

      if (logAction) return logAction;

      const label = getButtonLabel();
      if (label) {
        const base = label.replace(/\s+/g, "_").replace(/[^\w]/g, "");
        return isLink ? `${LogEventTypeEnum.USER_INTERACTION}: ${base}` : base;
      }

      return isLink
        ? target === "_blank"
          ? "EXTERNAL_LINK_CLICKED"
          : "LINK_CLICKED"
        : type;
    };

    /**
     * Build log context. Includes buttonText for buildButtonActionTitle. For anchors, includes href and isExternal.
     */
    const buildLogContext = () => {
      const hrefValue = href ?? (props as { href?: string })?.href;

      // Context policy:
      // - Only include minimal fields for links:
      //   - website/email links: { url: "<value>" }
      //   - phone links (tel:): { phone: "<value>" }
      // - Do not include any other key-values for links (no href/isExternal/linkType/linkValue/linkSource/dialog).
      if (isLink && hrefValue) {
        const lower = hrefValue.toLowerCase();
        if (lower.startsWith("tel:")) {
          return { phone: hrefValue.replace(/^tel:/i, "") };
        }
        return { url: hrefValue.replace(/^mailto:/i, "") };
      }

      // If caller provided explicit link metadata (e.g. "Call Now" button that opens tel:),
      // include only the minimal key for that link type, plus any caller-provided logMetadata.
      if (logLinkMeta) {
        const v = logLinkMeta.value ?? "";
        const linkCtx =
          logLinkMeta.type === "phone" ? { phone: v } : { url: v };
        return { ...linkCtx, ...logMetadata };
      }

      return {
        // buttonText: getButtonLabel(),
        ...logMetadata,
      };
    };

    /**
     * Main click handler with logging. Works for both button and anchor.
     */
    const handleClick = useCallback(
      async (e: React.MouseEvent<HTMLElement>) => {
        if (disabled || isLoading) {
          e.preventDefault();
          return;
        }

        const actionName = getActionName();
        const logContext = buildLogContext();

        if (type === "submit" && "form" in e.currentTarget) {
          const formEl = (e.currentTarget as HTMLButtonElement).form;

          if (formEl) {
            const formContext = getFormContextFromFormElement(formEl);
            loggerContextManager.setFormContext(formContext);
          }
        }

        try {
          const isTabTrigger = e.currentTarget.getAttribute?.("role") === "tab";

          if (!disableLogging && !isTabTrigger) {
            logUserAction(actionName, logContext, {
              debounceMs: logDebounceMs,
              suppressDuplicate: true,
              tag: logTag,
              category: logCategory,
              subCategory: logSubCategory,
              includeFormContext: type === "submit",
              useTitleAsIs: isLink,
            });
          }

          if (onClick) {
            setIsLoading(true);
            const result: unknown = onClick(
              e as React.MouseEvent<HTMLButtonElement>,
            );
            if (result instanceof Promise) {
              await result;
            }
          }
        } catch (error) {
          console.error("Error loggin user action: ", error);
          throw error;
        } finally {
          setIsLoading(false);
        }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [
        disabled,
        isLoading,
        disableLogging,
        logDebounceMs,
        logCategory,
        logSubCategory,
        logMetadata,
        logUserAction,
        onClick,
        children,
        type,
        ariaLabel,
        title,
        href,
        target,
        isLink,
      ],
    );

    const buttonClassName = isLink
      ? linkStyle === "button"
        ? cn(
            buttonVariants({ variant, size, className }),
            (disabled || isLoading) && "pointer-events-none opacity-50",
          )
        : cn(
            // Default link-like appearance for anchors.
            "text-primary underline-offset-4 hover:underline",
            className,
            (disabled || isLoading) && "pointer-events-none opacity-50",
          )
      : cn(
          buttonVariants({ variant, size, className }),
          (disabled || isLoading) && "pointer-events-none opacity-50",
        );

    if (isLink) {
      const resolvedRel =
        rel ?? (target === "_blank" ? "noopener noreferrer" : undefined);
      return (
        <a
          ref={ref as React.Ref<HTMLAnchorElement>}
          href={href}
          target={target}
          rel={resolvedRel}
          aria-disabled={disabled || isLoading}
          className={buttonClassName}
          onClick={handleClick}
          aria-label={ariaLabel}
          title={title}
          {...(props as React.AnchorHTMLAttributes<HTMLAnchorElement>)}
        >
          {children}
        </a>
      );
    }

    return (
      <Comp
        // @ts-expect-error Slot's polymorphic ref type conflicts with our union ref
        ref={ref}
        type={type}
        disabled={disabled || isLoading}
        className={buttonClassName}
        onClick={handleClick}
        aria-label={ariaLabel}
        title={title}
        {...props}
      >
        {children}
      </Comp>
    );
  },
);

Button.displayName = "Button";

export { Button, buttonVariants };
